import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft, Check } from 'lucide-react';
import { Badge, Button, Input } from '@caratuva/ui';
import { CreateConnectedAccountInput } from '@caratuva/shared-types';
import type {
  ConnectedAccountDTO,
  ConnectedAccountEntityType,
  PayoutBankAccountInput,
  RequestConnectedAccountTosDTO,
  SubmitConnectedAccountInput,
  TosDelivery,
} from '@caratuva/shared-types';
import {
  createConnectedAccount,
  requestConnectedAccountTos,
  uploadConnectedAccountFile,
} from '../lib/api';
import { getCountryOptions } from '../lib/country-codes';
import { Field, selectClass } from '../components/invoice-form';
import { BankAccountFields } from '../components/bank-account-fields';
import {
  KybForm,
  KycForm,
  emptyBiz,
  emptyKyc,
  emptyOwner,
  kybValueToSubmitBuyerBusiness,
  kybValueToSubmitSellerBusiness,
  kycValueToSubmit,
  type UploadKind,
} from '../components/kyb-form';
import { CARD_CLASS, CopyableField, FormError, StepHeader, Stepper } from '../components/wizard';
import { DeliveryChoice } from '../components/tos-delivery';

/*
 * Customer onboarding wizard (integrator-admin). Unifies what used to be the
 * separate buyer + seller wizards into one "customer" flow. Every customer is
 * created as a connected account (role=seller under the hood, so it can be
 * invoiced once it has a bank account) and verified with KYC (individual) or
 * KYB (business):
 *
 *   1. Details      — create the connected-account shell (individual|business).
 *   2. Verification — capture the dossier via the shared KycForm / KybForm.
 *   3. Terms        — choose ToS delivery (email/link) and OPTIONALLY register a
 *                     bank account (PIX), then mint the acceptance link.
 *   4. Done         — show the copyable acceptUrl (+ email confirmation).
 *
 * The customer accepts the settlement partner's ToS asynchronously; the
 * submission (and payout, if a bank account was provided) fires automatically
 * once they accept. A customer with a bank account becomes invoice-able
 * (ready to collect); a customer without one is verified only — a bank account
 * can be added later from the customer detail page.
 */

export const Route = createFileRoute('/_app/customers/new')({
  component: NewCustomer,
});

type Step = 'create' | 'verify' | 'terms' | 'done';

// The wizard uploads through the connected-account signed-URL endpoint, bound to
// this account id; the shared KycForm/KybForm take the upload fn as a prop.
function makeUpload(accountId: string) {
  return (file: File, kind: UploadKind, ownerIndex?: number): Promise<string> =>
    uploadConnectedAccountFile(accountId, file, kind, ownerIndex);
}

function NewCustomer(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();
  const [account, setAccount] = useState<ConnectedAccountDTO | null>(null);
  const [submit, setSubmit] = useState<SubmitConnectedAccountInput | null>(null);
  const [result, setResult] = useState<RequestConnectedAccountTosDTO | null>(null);
  const [delivery, setDelivery] = useState<TosDelivery>('email');
  const [step, setStep] = useState<Step>('create');

  function refreshAccount(next: ConnectedAccountDTO): void {
    setAccount(next);
    void qc.invalidateQueries({ queryKey: ['connected-accounts'] });
  }

  return (
    <section className="mx-auto max-w-3xl space-y-8">
      <div>
        <Link
          to="/customers"
          className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
        >
          <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
          {intl.formatMessage({ id: 'customers.title', defaultMessage: 'Customers' })}
        </Link>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'customers.new.title', defaultMessage: 'Onboard a customer' })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {intl.formatMessage({
            id: 'customers.new.subtitle',
            defaultMessage:
              'Register and verify a customer. Individuals verify by KYC, businesses by KYB. Add a bank account to make the customer invoice-able. Verification is reviewed by our settlement partner.',
          })}
        </p>
      </div>

      <CustomerStepper current={step} />

      {step === 'create' ? (
        <CreateStep
          onCreated={(acc) => {
            refreshAccount(acc);
            setStep('verify');
          }}
        />
      ) : null}

      {step === 'verify' && account ? (
        <VerifyStep
          account={account}
          onCaptured={(captured) => {
            setSubmit(captured);
            setStep('terms');
          }}
        />
      ) : null}

      {step === 'terms' && account && submit ? (
        <TermsStep
          account={account}
          submit={submit}
          delivery={delivery}
          onDeliveryChange={setDelivery}
          onDone={(res) => {
            setResult(res);
            setStep('done');
          }}
        />
      ) : null}

      {step === 'done' && account && result ? (
        <DoneStep account={account} result={result} delivery={delivery} />
      ) : null}
    </section>
  );
}

// ─── Stepper ─────────────────────────────────────────────────────────────────

const STEP_ORDER: Step[] = ['create', 'verify', 'terms'];

function CustomerStepper({ current }: { current: Step }): JSX.Element {
  const intl = useIntl();
  const labels: Record<Step, string> = {
    create: intl.formatMessage({ id: 'customers.new.step.create', defaultMessage: 'Details' }),
    verify: intl.formatMessage({
      id: 'customers.new.step.verify',
      defaultMessage: 'Verification',
    }),
    terms: intl.formatMessage({ id: 'customers.new.step.terms', defaultMessage: 'Terms' }),
    done: intl.formatMessage({ id: 'customers.new.step.done', defaultMessage: 'Done' }),
  };
  const currentIndex = current === 'done' ? STEP_ORDER.length : STEP_ORDER.indexOf(current);
  return (
    <Stepper
      steps={STEP_ORDER.map((key) => ({ key, label: labels[key] }))}
      currentIndex={currentIndex}
    />
  );
}

// ─── Step 1: Create ──────────────────────────────────────────────────────────

const ENTITY_TYPES: ConnectedAccountEntityType[] = ['individual', 'business'];

function CreateStep({ onCreated }: { onCreated: (acc: ConnectedAccountDTO) => void }): JSX.Element {
  const intl = useIntl();
  const countryOptions = useMemo(() => getCountryOptions(intl.locale), [intl.locale]);
  const [entityType, setEntityType] = useState<ConnectedAccountEntityType>('business');
  const [country, setCountry] = useState('BR');
  const [externalId, setExternalId] = useState('');
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const parsed = CreateConnectedAccountInput.parse({
        // Customers are created as sellers so they can be invoiced once a bank
        // account is attached; the buyer/seller split is not exposed in the UI.
        role: 'seller',
        entityType,
        externalId: externalId.trim(),
        legalName: legalName.trim() || undefined,
        displayName: displayName.trim() || undefined,
        email: email.trim(),
        country,
      });
      return createConnectedAccount(parsed);
    },
    onSuccess: onCreated,
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!externalId.trim() || !email.trim() || create.isPending) return;
    create.mutate();
  }

  return (
    <form onSubmit={onSubmit} className={CARD_CLASS}>
      <StepHeader
        title={intl.formatMessage({
          id: 'customers.new.create.title',
          defaultMessage: 'Step 1 — Customer details',
        })}
        description={intl.formatMessage({
          id: 'customers.new.create.description',
          defaultMessage:
            'Your own reference (external ID) and the customer’s identity. Individuals verify by KYC; businesses by KYB.',
        })}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          id="entityType"
          label={intl.formatMessage({
            id: 'customers.new.field.entityType',
            defaultMessage: 'Customer type',
          })}
          required
        >
          <select
            id="entityType"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as ConnectedAccountEntityType)}
            className={selectClass}
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'individual'
                  ? intl.formatMessage({
                      id: 'customers.new.entityType.individual',
                      defaultMessage: 'Individual',
                    })
                  : intl.formatMessage({
                      id: 'customers.new.entityType.business',
                      defaultMessage: 'Business',
                    })}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="country"
          label={intl.formatMessage({
            id: 'customers.new.field.country',
            defaultMessage: 'Country',
          })}
          required
        >
          <select
            id="country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className={selectClass}
          >
            {countryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="externalId"
          label={intl.formatMessage({
            id: 'customers.new.field.externalId',
            defaultMessage: 'External ID',
          })}
          required
        >
          <Input
            id="externalId"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'customers.new.field.externalId.placeholder',
              defaultMessage: 'Your reference for this customer',
            })}
            required
          />
        </Field>
        <Field
          id="legalName"
          label={
            entityType === 'business'
              ? intl.formatMessage({
                  id: 'customers.new.field.legalName',
                  defaultMessage: 'Legal name',
                })
              : intl.formatMessage({
                  id: 'customers.new.field.fullName',
                  defaultMessage: 'Full name',
                })
          }
        >
          <Input id="legalName" value={legalName} onChange={(e) => setLegalName(e.target.value)} />
        </Field>
        <Field
          id="displayName"
          label={intl.formatMessage({
            id: 'customers.new.field.displayName',
            defaultMessage: 'Display name',
          })}
        >
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <Field
          id="email"
          label={intl.formatMessage({
            id: 'customers.new.field.email',
            defaultMessage: 'Email',
          })}
          required
        >
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      <FormError error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={!externalId.trim() || !email.trim() || create.isPending}>
          {create.isPending
            ? intl.formatMessage({
                id: 'customers.new.create.saving',
                defaultMessage: 'Creating...',
              })
            : intl.formatMessage({
                id: 'customers.new.create.submit',
                defaultMessage: 'Create & continue',
              })}
        </Button>
      </div>
    </form>
  );
}

// ─── Step 2: Verify (capture KYC / KYB via the shared forms) ─────────────────

function VerifyStep({
  account,
  onCaptured,
}: {
  account: ConnectedAccountDTO;
  onCaptured: (submit: SubmitConnectedAccountInput) => void;
}): JSX.Element {
  const intl = useIntl();
  const isBusiness = account.entityType === 'business';
  const country = account.country ?? 'BR';

  if (isBusiness) {
    const initialBiz = {
      ...emptyBiz(),
      legalName: account.legalName ?? '',
      email: account.email ?? '',
      country,
    };
    return (
      <KybForm
        initialBiz={initialBiz}
        initialOwners={[emptyOwner()]}
        country={country}
        submitting={false}
        submitLabel={intl.formatMessage({
          id: 'customers.new.verify.continue',
          defaultMessage: 'Continue to terms',
        })}
        submittingLabel={intl.formatMessage({
          id: 'customers.new.verify.continue',
          defaultMessage: 'Continue to terms',
        })}
        heading={
          <StepHeader
            title={intl.formatMessage({
              id: 'customers.new.verify.business.title',
              defaultMessage: 'Step 2 — Business verification',
            })}
            description={intl.formatMessage({
              id: 'customers.new.verify.business.description',
              defaultMessage:
                'Business identity and documents the settlement partner reviews (KYB).',
            })}
          />
        }
        upload={makeUpload(account.id)}
        onSubmit={(value) =>
          onCaptured(
            country === 'BR'
              ? kybValueToSubmitSellerBusiness(value)
              : kybValueToSubmitBuyerBusiness(value),
          )
        }
      />
    );
  }

  const initialKyc = { ...emptyKyc(), email: account.email ?? '', country };
  return (
    <KycForm
      initialValue={initialKyc}
      country={country}
      submitting={false}
      submitLabel={intl.formatMessage({
        id: 'customers.new.verify.continue',
        defaultMessage: 'Continue to terms',
      })}
      submittingLabel={intl.formatMessage({
        id: 'customers.new.verify.continue',
        defaultMessage: 'Continue to terms',
      })}
      heading={
        <StepHeader
          title={intl.formatMessage({
            id: 'customers.new.verify.individual.title',
            defaultMessage: 'Step 2 — Identity verification',
          })}
          description={intl.formatMessage({
            id: 'customers.new.verify.individual.description',
            defaultMessage: 'Personal identity and documents the settlement partner reviews (KYC).',
          })}
        />
      }
      upload={makeUpload(account.id)}
      onSubmit={(value) => onCaptured(kycValueToSubmit(value))}
    />
  );
}

// ─── Step 3: Terms (ToS delivery) + optional bank account ────────────────────

function TermsStep({
  account,
  submit,
  delivery,
  onDeliveryChange,
  onDone,
}: {
  account: ConnectedAccountDTO;
  submit: SubmitConnectedAccountInput;
  delivery: TosDelivery;
  onDeliveryChange: (d: TosDelivery) => void;
  onDone: (result: RequestConnectedAccountTosDTO) => void;
}): JSX.Element {
  const intl = useIntl();
  const [addBank, setAddBank] = useState(true);
  const [payout, setPayout] = useState<PayoutBankAccountInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Final step: hand the captured KYC/KYB submission + (optional) payout +
  // delivery choice to request-tos, which captures them and mints the
  // ToS-acceptance link. No settlement-partner call fires until the customer
  // accepts.
  const finish = useMutation({
    mutationFn: () =>
      requestConnectedAccountTos(account.id, {
        submit,
        ...(addBank && payout ? { payout } : {}),
        delivery,
      }),
    onSuccess: (res) => onDone(res),
    onError: (err: Error) => setError(err.message),
  });

  const bankIncomplete = addBank && payout == null;

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    if (bankIncomplete || finish.isPending) return;
    finish.mutate();
  }

  return (
    <form onSubmit={onSubmit} className={CARD_CLASS}>
      <StepHeader
        title={intl.formatMessage({
          id: 'customers.new.terms.title',
          defaultMessage: 'Step 3 — Terms & bank account',
        })}
        description={intl.formatMessage({
          id: 'customers.new.terms.description',
          defaultMessage:
            'The customer accepts the settlement partner’s terms before verification. Optionally register a bank account now so the customer can be invoiced — you can also add one later.',
        })}
      />

      <label className="flex items-start gap-3 rounded-md border border-navy-200 bg-white p-4">
        <input
          type="checkbox"
          checked={addBank}
          onChange={(e) => setAddBank(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-medium text-navy-900">
            {intl.formatMessage({
              id: 'customers.new.bank.toggle',
              defaultMessage: 'Register a bank account now',
            })}
          </span>
          <span className="mt-0.5 block text-xs text-navy-600">
            {intl.formatMessage({
              id: 'customers.new.bank.toggle.hint',
              defaultMessage:
                'Required for the customer to receive invoice settlement. Skip to verify only.',
            })}
          </span>
        </span>
      </label>

      {addBank ? (
        <BankAccountFields
          onChange={setPayout}
          {...(account.legalName ? { defaultName: account.legalName } : {})}
        />
      ) : null}

      <DeliveryChoice
        value={delivery}
        onChange={onDeliveryChange}
        emailLabel={intl.formatMessage({
          id: 'customers.new.delivery.email',
          defaultMessage: 'Email the customer',
        })}
        emailHint={intl.formatMessage({
          id: 'customers.new.delivery.email.hint',
          defaultMessage:
            'We email the customer a single-use link to accept the settlement partner’s terms.',
        })}
        linkLabel={intl.formatMessage({
          id: 'customers.new.delivery.link',
          defaultMessage: 'I’ll send the link myself',
        })}
        linkHint={intl.formatMessage({
          id: 'customers.new.delivery.link.hint',
          defaultMessage: 'We return the acceptance link on the next step for you to deliver.',
        })}
      />

      <FormError error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={bankIncomplete || finish.isPending}>
          {finish.isPending
            ? intl.formatMessage({
                id: 'customers.new.terms.sending',
                defaultMessage: 'Sending...',
              })
            : delivery === 'email'
              ? intl.formatMessage({
                  id: 'customers.new.terms.finish.email',
                  defaultMessage: 'Finish & email customer',
                })
              : intl.formatMessage({
                  id: 'customers.new.terms.finish.link',
                  defaultMessage: 'Finish & get link',
                })}
        </Button>
      </div>
    </form>
  );
}

// ─── Step 4: Done ────────────────────────────────────────────────────────────

function DoneStep({
  account,
  result,
  delivery,
}: {
  account: ConnectedAccountDTO;
  result: RequestConnectedAccountTosDTO;
  delivery: TosDelivery;
}): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const emailed = delivery === 'email' && result.emailSent;

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check aria-hidden className="h-5 w-5" strokeWidth={2.5} />
        </span>
        <div>
          <h2 className="text-base font-semibold text-navy-900">
            {intl.formatMessage({
              id: 'customers.new.done.title',
              defaultMessage: 'Customer onboarded',
            })}
          </h2>
          <p className="text-sm text-navy-600">
            {intl.formatMessage({
              id: 'customers.new.done.description',
              defaultMessage:
                'Verification starts automatically once the customer accepts the settlement partner’s terms. A customer with a bank account can be invoiced once it is ready to collect.',
            })}
          </p>
        </div>
      </div>

      {emailed ? (
        <p className="text-sm text-navy-700">
          {intl.formatMessage(
            {
              id: 'customers.new.done.emailed',
              defaultMessage: 'We emailed the acceptance link to {email}.',
            },
            { email: account.email ?? '' },
          )}
        </p>
      ) : delivery === 'email' && !result.emailSent ? (
        <p className="text-sm text-amber-700">
          {intl.formatMessage({
            id: 'customers.new.done.emailFailed',
            defaultMessage:
              "We couldn't email the acceptance link — copy it below and send it to the customer yourself.",
          })}
        </p>
      ) : null}

      <CopyableField
        id="customerAcceptUrl"
        label={intl.formatMessage({
          id: 'customers.new.done.acceptUrl',
          defaultMessage: 'Terms acceptance link',
        })}
        value={result.acceptUrl}
      />

      <div className="flex items-center gap-2 text-sm">
        <span className="text-navy-700">
          {intl.formatMessage({ id: 'customers.new.done.status', defaultMessage: 'Status:' })}
        </span>
        <Badge variant="outline">
          {intl.formatMessage({
            id: 'customers.new.done.awaitingTerms',
            defaultMessage: 'Awaiting terms acceptance',
          })}
        </Badge>
      </div>

      <div className="flex justify-end gap-2">
        <Button asChild variant="outline">
          <Link to="/customers">
            {intl.formatMessage({
              id: 'customers.new.done.list',
              defaultMessage: 'Back to customers',
            })}
          </Link>
        </Button>
        <Button
          type="button"
          onClick={() => void navigate({ to: '/customers/$id', params: { id: account.id } })}
        >
          {intl.formatMessage({ id: 'customers.new.done.detail', defaultMessage: 'View customer' })}
        </Button>
      </div>
    </div>
  );
}
